import {
    type ReactNode,
    useState,
    useMemo
} from "react";
import {
    StyleSheet,
    View
} from "react-native";
import {
    type ContainerColorRole,
    type TypeColorVariant,
    type BorderColorRole,
    type StateColorType,
    type TitleColorRole,
    getColorRoleValue,
    STATE_COLOR_KEYS,
    TYPE_COLOR_ROLES,
    NCoreUIKitTheme,
    Button,
    Badge,
    Alert,
    Card,
    Chip,
    Text
} from "ncore-ui-kit";
import {
    useDocsI18n
} from "../../i18n";
import type {
    DocsTranslate
} from "../../anatomy/types";
import type {
    DocsTocItem
} from "../../components/toc";
import {
    PageHeader,
    BulletList,
    InlineCode,
    Paragraph,
    DocsTable,
    CodeBlock,
    SubTitle,
    Callout,
    Article,
    Section,
    Preview,
    Prose,
    Term
} from "../../components/content";

type ColorsType = NCoreUIKit.ActivePalette["colors"];

type PaletteKeyType = keyof NCoreUIKit.PaletteKey;

type ThemeKeyType = keyof NCoreUIKit.ThemeKey;

type ColorPickType = (colors: ColorsType) => string;

type RgbaType = {
    alpha: number;
    green: number;
    blue: number;
    red: number;
};

type NamedColorType = {
    getColor: ColorPickType;
    name: string;
};

type LadderRowType = NamedColorType & {
    step: number;
};

type TextGateType = "exempt" | "text" | "ui";

type TextGreyType = NamedColorType & {
    isApcaInfoInDark?: boolean;
    apcaTarget: number;
    gate: TextGateType;
};

type ContrastStepType = {
    getColor: (colors: ColorsType, family: StateColorType) => string;
    roles: string;
};

type RoleGroupType = {
    group: string;
    keys: string;
    use: string;
};

type CardVariantType = {
    variant: "outline" | "filled" | "inset";
    textKey: string;
};

type SurroundType = {
    induction: number;
    impact: number;
};

type ViewingType = {
    backgroundInduction: number;
    chromaInduction: number;
    backgroundRatio: number;
    luminanceLevel: number;
    whiteResponse: number;
    exponent: number;
};

type UcsType = {
    lightness: number;
    axisA: number;
    axisB: number;
};

const TERM_PATTERN = /\{\{term:([a-zA-Z0-9]+)\}\}/;

const RGBA_PATTERN = /rgba?\(([^)]+)\)/;

const JND_UNIT = 1.2;

const LADDER_STEP = 1.26;

const BACKGROUND_LUMINANCE = 20;

const SRGB_TO_XYZ = [
    [
        0.4123907992659595,
        0.357584339383878,
        0.1804807884018343
    ],
    [
        0.21263900587151036,
        0.715168678767756,
        0.07219231536073371
    ],
    [
        0.01933081871559185,
        0.11919477979462598,
        0.9505321522496606
    ]
];

const CAT16_MATRIX = [
    [
        0.401288,
        0.650173,
        -0.051461
    ],
    [
        -0.250268,
        1.204414,
        0.045854
    ],
    [
        -0.002079,
        0.048952,
        0.953127
    ]
];

const AVERAGE_SURROUND: SurroundType = {
    induction: 1,
    impact: 0.69
};

const DIM_SURROUND: SurroundType = {
    induction: 0.9,
    impact: 0.59
};

const DUV_COEFFICIENTS = [
    -0.471106,
    1.925865,
    -2.4243787,
    1.5317403,
    -0.5179722,
    0.0893944,
    -0.00616793
];

const FAMILIES: Array<StateColorType> = [
    "primary",
    "danger",
    "warning",
    "success",
    "info",
    "neutral"
];

const BRAND_AND_STATUS_FAMILIES: Array<StateColorType> = [
    "primary",
    "success",
    "warning",
    "danger",
    "info"
];

const VARIANTS: Array<TypeColorVariant> = [
    "filled",
    "tonal",
    "outline",
    "ghost"
];

const PALETTE_NAMES: Record<string, string> = {
    "nibgat-community": "NİBGAT Community",
    caycore: "CayCore",
    nibgat: "NİBGAT"
};

const GATE_LABELS: Record<Exclude<TextGateType, "exempt">, string> = {
    text: "4.5:1",
    ui: "3:1"
};

const PALETTE_STRIP: Array<ColorPickType> = [
    (colors) => colors.system.default.background,
    (colors) => colors.system.default.primary,
    (colors) => colors.system.default.secondary,
    (colors) => colors.system.default.tertiary,
    (colors) => colors.system.container.primary,
    (colors) => colors.content.text.high
];

const TEXT_SURFACES: Array<ColorPickType> = [
    (colors) => colors.content.container.default,
    (colors) => colors.content.container.raised,
    (colors) => colors.content.container.mid,
    (colors) => colors.content.container.subtle,
    (colors) => colors.content.container.selected
];

const ROLE_GROUPS: Array<RoleGroupType> = [
    {
        keys: "default · raised · mid · subtle · selected · inverse",
        use: "colorPage.roleContainer",
        group: "content.container"
    },
    {
        keys: "high · mid · low · danger · link · onPrimary",
        use: "colorPage.roleText",
        group: "content.text"
    },
    {
        keys: "subtle · default · hover · emphasized",
        use: "colorPage.roleBorder",
        group: "content.border"
    },
    {
        keys: "default · high · mid · low",
        use: "colorPage.roleIcon",
        group: "content.icon"
    },
    {
        group: "system.low · container · high",
        keys: "primary · error · neutral …",
        use: "colorPage.roleTint"
    },
    {
        keys: "primary · warning · background (default, on) · neutral (onContainer) …",
        group: "system.default · on · onContainer",
        use: "colorPage.roleOn"
    },
    {
        keys: "overlay.fill · overlay.hover · overlay.pressed · overlay.focus · disabled",
        use: "colorPage.roleState",
        group: "system.state"
    },
    {
        keys: "categorical · sequential · diverging",
        group: "chart · code · project",
        use: "colorPage.roleData"
    }
];

const LADDER_ROWS: Array<LadderRowType> = [
    {
        getColor: (colors) => colors.content.container.default,
        name: "container.default",
        step: 0
    },
    {
        getColor: (colors) => colors.content.container.raised,
        name: "container.raised",
        step: 0
    },
    {
        getColor: (colors) => colors.content.container.mid,
        name: "container.mid",
        step: 1
    },
    {
        getColor: (colors) => colors.content.container.subtle,
        name: "container.subtle",
        step: 2
    },
    {
        getColor: (colors) => colors.content.container.selected,
        name: "container.selected",
        step: 3
    },
    {
        getColor: (colors) => colors.content.border.subtle,
        name: "border.subtle · overlay",
        step: 4
    },
    {
        getColor: (colors) => colors.content.border.default,
        name: "border.default",
        step: 5
    },
    {
        getColor: (colors) => colors.content.border.hover,
        name: "border.hover",
        step: 6
    }
];

const DARK_ROWS: Array<NamedColorType> = [
    {
        getColor: (colors) => colors.content.container.raised,
        name: "container.raised"
    },
    {
        getColor: (colors) => colors.content.container.subtle,
        name: "container.subtle"
    },
    {
        getColor: (colors) => colors.content.container.selected,
        name: "container.selected"
    },
    {
        getColor: (colors) => colors.content.border.subtle,
        name: "border.subtle"
    },
    {
        getColor: (colors) => colors.content.border.default,
        name: "border.default"
    },
    {
        getColor: (colors) => colors.content.border.hover,
        name: "border.hover"
    }
];

const CARD_VARIANTS: Array<CardVariantType> = [
    {
        textKey: "colorPage.cardOutlineText",
        variant: "outline"
    },
    {
        textKey: "colorPage.cardFilledText",
        variant: "filled"
    },
    {
        textKey: "colorPage.cardInsetText",
        variant: "inset"
    }
];

const LOW_STEP_COLORS: Record<StateColorType, ColorPickType> = {
    primary: (colors) => colors.content.border.emphasized,
    success: (colors) => colors.content.text.successLow,
    warning: (colors) => colors.content.text.warningLow,
    danger: (colors) => colors.content.text.dangerLow,
    info: (colors) => colors.content.text.infoLow,
    neutral: (colors) => colors.content.text.low
};

const DEFAULT_STEP_COLORS: Record<StateColorType, ColorPickType> = {
    neutral: (colors) => colors.content.container.neutral,
    primary: (colors) => colors.system.default.primary,
    success: (colors) => colors.system.default.success,
    warning: (colors) => colors.system.default.warning,
    danger: (colors) => colors.system.default.error,
    info: (colors) => colors.system.default.info
};

const CONTRAST_STEPS: Array<ContrastStepType> = [
    {
        getColor: (colors, family) => colors.system.low[STATE_COLOR_KEYS[family]],
        roles: "low"
    },
    {
        getColor: (colors, family) => colors.system.container[STATE_COLOR_KEYS[family]],
        roles: "container"
    },
    {
        getColor: (colors, family) => colors.system.high[STATE_COLOR_KEYS[family]],
        roles: "high"
    },
    {
        getColor: (colors, family) => LOW_STEP_COLORS[family](colors),
        roles: "*Low · focus"
    },
    {
        getColor: (colors, family) => DEFAULT_STEP_COLORS[family](colors),
        roles: "default"
    },
    {
        getColor: (colors, family) => colors.system.onContainer[STATE_COLOR_KEYS[family]],
        roles: "onContainer"
    }
];

const GREY_TOKENS: Array<NamedColorType> = [
    {
        getColor: (colors) => colors.content.container.default,
        name: "container.default"
    },
    {
        getColor: (colors) => colors.content.container.raised,
        name: "container.raised"
    },
    {
        getColor: (colors) => colors.content.container.mid,
        name: "container.mid"
    },
    {
        getColor: (colors) => colors.content.container.subtle,
        name: "container.subtle"
    },
    {
        getColor: (colors) => colors.content.border.default,
        name: "border.default"
    },
    {
        getColor: (colors) => colors.content.text.high,
        name: "text.high"
    },
    {
        getColor: (colors) => colors.content.text.mid,
        name: "text.mid"
    },
    {
        getColor: (colors) => colors.content.icon.low,
        name: "icon.low"
    }
];

const TEXT_GREYS: Array<TextGreyType> = [
    {
        getColor: (colors) => colors.content.text.high,
        name: "text.high",
        apcaTarget: 75,
        gate: "text"
    },
    {
        getColor: (colors) => colors.content.text.mid,
        isApcaInfoInDark: true,
        name: "text.mid",
        apcaTarget: 60,
        gate: "text"
    },
    {
        getColor: (colors) => colors.content.text.low,
        isApcaInfoInDark: true,
        name: "text.low",
        apcaTarget: 60,
        gate: "text"
    },
    {
        getColor: (colors) => colors.content.icon.default,
        name: "icon.default",
        apcaTarget: 45,
        gate: "ui"
    },
    {
        getColor: (colors) => colors.content.icon.mid,
        name: "icon.mid",
        apcaTarget: 45,
        gate: "ui"
    },
    {
        getColor: (colors) => colors.content.icon.low,
        name: "icon.low",
        apcaTarget: 45,
        gate: "ui"
    },
    {
        getColor: (colors) => colors.content.text.disabled,
        isApcaInfoInDark: true,
        name: "text.disabled",
        gate: "exempt",
        apcaTarget: 30
    }
];

const ALIAS_ROWS: Array<{
    getCanonical: ColorPickType;
    getAlias: ColorPickType;
    canonical: string;
    alias: string;
}> = [
    {
        getAlias: (colors) => colors.system.onMid.surface,
        getCanonical: (colors) => colors.content.text.mid,
        alias: "system.onMid.surface",
        canonical: "content.text.mid"
    },
    {
        getAlias: (colors) => colors.system.onLow.surface,
        getCanonical: (colors) => colors.content.text.low,
        alias: "system.onLow.surface",
        canonical: "content.text.low"
    },
    {
        getCanonical: (colors) => colors.content.container.subtle,
        getAlias: (colors) => colors.system.variant.surface,
        canonical: "content.container.subtle",
        alias: "system.variant.surface"
    },
    {
        getAlias: (colors) => colors.system.onVariant.surface,
        getCanonical: (colors) => colors.content.text.mid,
        alias: "system.onVariant.surface",
        canonical: "content.text.mid"
    },
    {
        getCanonical: (colors) => colors.content.text.high,
        getAlias: (colors) => colors.system.on.surface,
        canonical: "content.text.high",
        alias: "system.on.surface"
    },
    {
        getCanonical: (colors) => colors.content.text.disabled,
        getAlias: (colors) => colors.system.disabled.content,
        canonical: "content.text.disabled",
        alias: "system.disabled.content"
    },
    {
        getAlias: (colors) => colors.system.state.content.mid.error,
        getCanonical: (colors) => colors.content.text.danger,
        alias: "system.state.content.mid.error",
        canonical: "content.text.danger"
    }
];

const OVERLAY_ROWS: Array<NamedColorType> = [
    {
        getColor: (colors) => colors.system.state.overlay.fill.default,
        name: "fill.default"
    },
    {
        getColor: (colors) => colors.system.state.overlay.hover.default,
        name: "hover.default"
    },
    {
        getColor: (colors) => colors.system.state.overlay.pressed.default,
        name: "pressed.default"
    },
    {
        getColor: (colors) => colors.system.state.overlay.fill.primary,
        name: "fill.primary"
    },
    {
        getColor: (colors) => colors.system.state.overlay.hover.primary,
        name: "hover.primary"
    },
    {
        getColor: (colors) => colors.system.state.overlay.pressed.primary,
        name: "pressed.primary"
    }
];

const CONTRAST_CODE = `const toLinear = (channel: number) => {
    const value = channel / 255;

    return value <= 0.04045 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
};

const getLuminance = (color: RgbaType) => 0.2126 * toLinear(color.red)
    + 0.7152 * toLinear(color.green)
    + 0.0722 * toLinear(color.blue);

const getContrastRatio = (foreground: string, background: string) => {
    const base = parseColor(background);

    const front = compositeColor(parseColor(foreground), base);

    const lighter = Math.max(getLuminance(front), getLuminance(base));

    const darker = Math.min(getLuminance(front), getLuminance(base));

    return (lighter + 0.05) / (darker + 0.05);
};`;

const COLOR_CODE = `const {
    colors
} = NCoreUIKitTheme.useContext();

const style = {
    backgroundColor: colors.content.container.raised,
    borderColor: colors.content.border.subtle,
    color: colors.content.text.high
};`;

const COMPONENT_CODE = `<Button
    onPress={handleDeletePress}
    title={deleteTitle}
    variant="tonal"
    type="danger"
/>

<Text
    color="low"
>
    {description}
</Text>`;

const handlePreviewPress = () => undefined;

const multiplyMatrix = (matrix: Array<Array<number>>, vector: Array<number>) => matrix.map((row) => row.reduce((sum, value, index) => sum + value * (vector[index] ?? 0), 0));

const parseColor = (value: string): RgbaType => {
    const match = RGBA_PATTERN.exec(value);

    if(match) {
        const [
            red = 0,
            green = 0,
            blue = 0,
            alpha = 1
        ] = (match[1] ?? "").split(",").map((part) => Number(part.trim()));

        return {
            alpha,
            green,
            blue,
            red
        };
    }

    const hex = value.replace("#", "");

    return {
        green: parseInt(hex.slice(2, 4), 16),
        blue: parseInt(hex.slice(4, 6), 16),
        red: parseInt(hex.slice(0, 2), 16),
        alpha: 1
    };
};

const toLinear = (channel: number) => {
    const value = channel / 255;

    return value <= 0.04045 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
};

const getLuminance = (color: RgbaType) => 0.2126 * toLinear(color.red) + 0.7152 * toLinear(color.green) + 0.0722 * toLinear(color.blue);

const compositeColor = (top: RgbaType, bottom: RgbaType): RgbaType => ({
    green: bottom.green + (top.green - bottom.green) * top.alpha,
    blue: bottom.blue + (top.blue - bottom.blue) * top.alpha,
    red: bottom.red + (top.red - bottom.red) * top.alpha,
    alpha: 1
});

const getContrastRatio = (foreground: string, background: string) => {
    const base = parseColor(background);

    const front = compositeColor(parseColor(foreground), base);

    const lighter = Math.max(getLuminance(front), getLuminance(base));

    const darker = Math.min(getLuminance(front), getLuminance(base));

    return (lighter + 0.05) / (darker + 0.05);
};

const getApcaLuminance = (color: RgbaType) => {
    const luminance = 0.2126729 * (color.red / 255) ** 2.4 + 0.7151522 * (color.green / 255) ** 2.4 + 0.072175 * (color.blue / 255) ** 2.4;

    return luminance < 0.022 ? luminance + (0.022 - luminance) ** 1.414 : luminance;
};

const getApcaContrast = (foreground: string, background: string) => {
    const base = parseColor(background);

    const text = getApcaLuminance(compositeColor(parseColor(foreground), base));

    const back = getApcaLuminance(base);

    if(Math.abs(back - text) < 0.0005) {
        return 0;
    }

    if(back > text) {
        const contrast = (back ** 0.56 - text ** 0.57) * 1.14;

        return contrast < 0.1 ? 0 : (contrast - 0.027) * 100;
    }

    const contrast = (back ** 0.65 - text ** 0.62) * 1.14;

    return contrast > -0.1 ? 0 : (contrast + 0.027) * 100;
};

const toRgbString = (color: RgbaType) => `rgb(${Math.round(color.red)}, ${Math.round(color.green)}, ${Math.round(color.blue)})`;

const getCompositeColor = (top: string, bottom: string) => toRgbString(compositeColor(parseColor(top), parseColor(bottom)));

const WHITE_XYZ = multiplyMatrix(SRGB_TO_XYZ, [
    1,
    1,
    1
]).map((value) => value * 100);

const WHITE_LUMINANCE = WHITE_XYZ[1] ?? 100;

const WHITE_CONE = multiplyMatrix(CAT16_MATRIX, WHITE_XYZ);

const getAdaptedResponse = (value: number, luminanceLevel: number) => {
    const scaled = (luminanceLevel * Math.abs(value) / 100) ** 0.42;

    return 400 * Math.sign(value) * scaled / (27.13 + scaled) + 0.1;
};

const getViewing = (adaptingLuminance: number, surround: SurroundType): ViewingType => {
    const factor = 1 / (5 * adaptingLuminance + 1);

    const luminanceLevel = 0.2 * factor ** 4 * 5 * adaptingLuminance + 0.1 * (1 - factor ** 4) ** 2 * Math.cbrt(5 * adaptingLuminance);

    const backgroundRatio = BACKGROUND_LUMINANCE / WHITE_LUMINANCE;

    const backgroundInduction = 0.725 * (1 / backgroundRatio) ** 0.2;

    const whiteAdapted = getAdaptedResponse(WHITE_LUMINANCE, luminanceLevel);

    return {
        whiteResponse: (3.05 * whiteAdapted - 0.305) * backgroundInduction,
        exponent: surround.impact * (1.48 + Math.sqrt(backgroundRatio)),
        chromaInduction: surround.induction,
        backgroundInduction,
        backgroundRatio,
        luminanceLevel
    };
};

const LIGHT_VIEWING = getViewing(64, AVERAGE_SURROUND);

const DARK_VIEWING = getViewing(8, DIM_SURROUND);

const getThemeViewing = (themeKey: string) => themeKey === "dark" ? DARK_VIEWING : LIGHT_VIEWING;

const getUcs = (value: string, viewing: ViewingType): UcsType => {
    const color = parseColor(value);

    const xyz = multiplyMatrix(SRGB_TO_XYZ, [
        toLinear(color.red),
        toLinear(color.green),
        toLinear(color.blue)
    ]).map((channel) => channel * 100);

    const [
        red = 0,
        green = 0,
        blue = 0
    ] = multiplyMatrix(CAT16_MATRIX, xyz).map((channel, index) => getAdaptedResponse(channel * WHITE_LUMINANCE / (WHITE_CONE[index] ?? 1), viewing.luminanceLevel));

    const axisA = red - 12 * green / 11 + blue / 11;

    const axisB = (red + green - 2 * blue) / 9;

    const hue = Math.atan2(axisB, axisA);

    const eccentricity = 0.25 * (Math.cos(hue + 2) + 3.8);

    const achromatic = (2 * red + green + 0.05 * blue - 0.305) * viewing.backgroundInduction;

    const lightness = 100 * Math.max(achromatic / viewing.whiteResponse, 0) ** viewing.exponent;

    const magnitude = 50000 / 13 * viewing.chromaInduction * viewing.backgroundInduction * eccentricity * Math.hypot(axisA, axisB) / (red + green + 21 / 20 * blue);

    const chroma = magnitude ** 0.9 * Math.sqrt(lightness / 100) * (1.64 - 0.29 ** viewing.backgroundRatio) ** 0.73;

    const colorfulness = Math.log(1 + 0.0228 * chroma * viewing.luminanceLevel ** 0.25) / 0.0228;

    return {
        lightness: 1.7 * lightness / (1 + 0.007 * lightness),
        axisA: colorfulness * Math.cos(hue),
        axisB: colorfulness * Math.sin(hue)
    };
};

const getJnd = (first: string, second: string, viewing: ViewingType) => {
    const one = getUcs(getCompositeColor(first, second), viewing);

    const two = getUcs(second, viewing);

    return Math.hypot(one.lightness - two.lightness, one.axisA - two.axisA, one.axisB - two.axisB) / JND_UNIT;
};

const getOklchChroma = (value: string) => {
    const color = parseColor(value);

    const red = toLinear(color.red);

    const green = toLinear(color.green);

    const blue = toLinear(color.blue);

    const long = Math.cbrt(0.4122214708 * red + 0.5363325363 * green + 0.0514459929 * blue);

    const medium = Math.cbrt(0.2119034982 * red + 0.6806995451 * green + 0.1073969566 * blue);

    const short = Math.cbrt(0.0883024619 * red + 0.2817188376 * green + 0.6299787005 * blue);

    const axisA = 1.9779984951 * long - 2.428592205 * medium + 0.4505937099 * short;

    const axisB = 0.0259040371 * long + 0.7827717662 * medium - 0.808675766 * short;

    return Math.hypot(axisA, axisB);
};

const getDuvFromLinear = (red: number, green: number, blue: number) => {
    const tristimulusX = 0.4124 * red + 0.3576 * green + 0.1805 * blue;

    const tristimulusY = 0.2126 * red + 0.7152 * green + 0.0722 * blue;

    const tristimulusZ = 0.0193 * red + 0.1192 * green + 0.9505 * blue;

    const denominator = tristimulusX + 15 * tristimulusY + 3 * tristimulusZ;

    if(denominator === 0) {
        return 0;
    }

    const chromaticityU = 4 * tristimulusX / denominator;

    const chromaticityV = 6 * tristimulusY / denominator;

    const distance = Math.hypot(chromaticityU - 0.292, chromaticityV - 0.24);

    const angle = Math.acos((chromaticityU - 0.292) / distance);

    const locus = DUV_COEFFICIENTS.reduce((sum, coefficient, index) => sum + coefficient * angle ** index, 0);

    return distance - locus;
};

const WHITE_DUV = getDuvFromLinear(1, 1, 1);

const getDeltaDuv = (value: string) => {
    const color = parseColor(value);

    return getDuvFromLinear(toLinear(color.red), toLinear(color.green), toLinear(color.blue)) - WHITE_DUV;
};

const getSpread = (ratios: Array<number>) => Math.max(...ratios) / Math.min(...ratios);

const makeBreakable = (text: string) => text.replace(/([.,])/g, "$1​");

const formatRatio = (ratio: number) => `${ratio.toFixed(2)}:1`;

const formatJnd = (value: number) => value.toFixed(2);

const getFilledColors = (colors: ColorsType, family: StateColorType) => {
    const roles = TYPE_COLOR_ROLES[family].filled;

    return {
        fill: getColorRoleValue({
            role: roles.containerColor,
            colors
        }),
        label: getColorRoleValue({
            role: roles.titleColor,
            colors
        })
    };
};

const getFilledLabelApca = (colors: ColorsType, family: StateColorType) => {
    const {
        label,
        fill
    } = getFilledColors(colors, family);

    return Math.abs(getApcaContrast(label, fill));
};

const formatSigned = (value: number) => {
    if(Math.abs(value) < 0.00005) {
        return "0.0000";
    }

    return value < 0 ? `−${Math.abs(value).toFixed(4)}` : `+${value.toFixed(4)}`;
};

const getRoleName = (role: ContainerColorRole | BorderColorRole | TitleColorRole) => role === "transparent" ? role : `${role.group}.${String(role.key)}`;

const renderTerms = (text: string): Array<ReactNode> => text.split(TERM_PATTERN).map((part, index) => index % 2 === 1 ? <Term
    key={`${part}-${index}`}
    id={part}
/> : part);

const stylesheet = StyleSheet.create({
    column: {
        alignItems: "flex-start",
        flexDirection: "column"
    },
    stretch: {
        flexDirection: "column",
        alignItems: "stretch"
    },
    wrapRow: {
        flexDirection: "row",
        alignItems: "center",
        flexWrap: "wrap"
    },
    inline: {
        flexDirection: "row",
        alignItems: "center"
    }
});

const useColorStyles = () => {
    const {
        shapeConfigs,
        avatarSizes,
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext();

    return useMemo(() => ({
        cardGrid: {
            gridTemplateColumns: `repeat(auto-fit, minmax(min(100%, ${shapeConfigs.contentMaxWidth / 4}px), 1fr))`,
            gap: spaces.spacingMd,
            display: "grid",
            width: "100%"
        },
        familyGrid: {
            gridTemplateColumns: `repeat(auto-fit, minmax(${avatarSizes.large}px, 1fr))`,
            gap: spaces.spacingMd,
            display: "grid",
            width: "100%"
        },
        largeSwatch: {
            borderColor: colors.content.border.default,
            height: avatarSizes.xSmall,
            width: avatarSizes.xLarge,
            borderWidth: borders.line,
            borderRadius: radiuses.sm
        },
        swatch: {
            borderColor: colors.content.border.default,
            height: avatarSizes.xxSmall,
            width: avatarSizes.xxSmall,
            borderWidth: borders.line,
            borderRadius: radiuses.xs
        },
        cell: {
            paddingVertical: spaces.spacingSm
        },
        tile: {
            width: avatarSizes.xLarge
        },
        gapMd: {
            gap: spaces.spacingMd
        },
        gapSm: {
            gap: spaces.spacingSm
        },
        gapXs: {
            gap: spaces.spacingXs
        }
    }) as const, [
        shapeConfigs,
        avatarSizes,
        radiuses,
        borders,
        colors,
        spaces
    ]);
};

const Swatch = ({
    isLarge = false,
    color
}: {
    isLarge?: boolean;
    color: string;
}) => {
    const styles = useColorStyles();

    return <View
        style={[
            isLarge ? styles.largeSwatch : styles.swatch,
            {
                backgroundColor: color
            }
        ]}
        aria-hidden={true}
    />;
};

const ValueText = ({
    children
}: {
    children: ReactNode;
}) => <Text
    variant="monoMediumSize"
    color="high"
>
    {children}
</Text>;

const ColorValue = ({
    color
}: {
    color: string;
}) => {
    const styles = useColorStyles();

    return <View
        style={[
            stylesheet.inline,
            styles.gapSm
        ]}
    >
        <Swatch
            color={color}
        />
        <ValueText>
            {color}
        </ValueText>
    </View>;
};

const RoleValue = ({
    color,
    name
}: {
    color: string;
    name: string;
}) => {
    const styles = useColorStyles();

    return <View
        style={[
            stylesheet.column,
            styles.gapXs
        ]}
    >
        {
            color === "transparent" ?
                null
            :
                <ColorValue
                    color={color}
                />
        }
        <Text
            variant="bodySmallSize"
            color="mid"
        >
            {makeBreakable(name)}
        </Text>
    </View>;
};

const TokenName = ({
    detail,
    name
}: {
    detail: string;
    name: string;
}) => {
    const styles = useColorStyles();

    return <View
        style={[
            stylesheet.column,
            styles.gapXs
        ]}
    >
        <InlineCode>
            {makeBreakable(name)}
        </InlineCode>
        <Text
            variant="bodySmallSize"
            color="mid"
        >
            {makeBreakable(detail)}
        </Text>
    </View>;
};

const ColorTable = ({
    headers,
    rows
}: {
    rows: Array<Array<ReactNode>>;
    headers: Array<string>;
}) => {
    const styles = useColorStyles();

    return <DocsTable
        rows={rows.map((row) => row.map((cell) => <View
            style={styles.cell}
        >
            {
                typeof cell === "string" ?
                    <Text
                        variant="bodyMediumSize"
                        color="high"
                    >
                        {cell}
                    </Text>
                :
                    cell
            }
        </View>))}
        headers={headers}
    />;
};

const TermList = ({
    items
}: {
    items: Array<string>;
}) => {
    const {
        t
    } = useDocsI18n();

    return <BulletList
        items={items.map((item) => renderTerms(t(item)))}
    />;
};

const RoleGroupTable = () => {
    const {
        t
    } = useDocsI18n();

    return <ColorTable
        rows={ROLE_GROUPS.map((row) => [
            <InlineCode
                key="group"
            >
                {row.group}
            </InlineCode>,
            t(row.use),
            <Text
                variant="bodySmallSize"
                color="mid"
                key="keys"
            >
                {row.keys}
            </Text>
        ])}
        headers={[
            t("colorPage.colGroup"),
            t("colorPage.colUse"),
            t("colorPage.colKeys")
        ]}
    />;
};

const VariantTable = () => {
    const {
        t
    } = useDocsI18n();

    const {
        colors
    } = NCoreUIKitTheme.useContext();

    const styles = useColorStyles();

    const [
        family,
        setFamily
    ] = useState<StateColorType>("primary");

    const handleFamilyPress = (item: StateColorType) => () => setFamily(item);

    const rows = VARIANTS.map((variant) => {
        const roles = TYPE_COLOR_ROLES[family][variant];

        const fill = getColorRoleValue({
            role: roles.containerColor,
            colors
        });

        const label = getColorRoleValue({
            role: roles.titleColor,
            colors
        });

        const border = getColorRoleValue({
            role: roles.borderColor,
            colors
        });

        const base = fill === "transparent" ? colors.content.container.default : getCompositeColor(fill, colors.content.container.default);

        return [
            <InlineCode
                key="variant"
            >
                {variant}
            </InlineCode>,
            <RoleValue
                name={getRoleName(roles.containerColor)}
                color={fill}
                key="fill"
            />,
            <RoleValue
                name={getRoleName(roles.titleColor)}
                color={label}
                key="label"
            />,
            <RoleValue
                name={getRoleName(roles.borderColor)}
                color={border}
                key="border"
            />,
            <ValueText
                key="ratio"
            >
                {formatRatio(getContrastRatio(label, base))}
            </ValueText>
        ];
    });

    return <View
        style={[
            stylesheet.stretch,
            styles.gapMd
        ]}
    >
        <View
            aria-label={t("colorPage.familyPickerLabel")}
            style={[
                stylesheet.wrapRow,
                styles.gapSm
            ]}
            role="group"
        >
            {
                FAMILIES.map((item) => <Chip
                    onPress={handleFamilyPress(item)}
                    isSelected={item === family}
                    variant="outline"
                    title={item}
                    key={item}
                />)
            }
        </View>
        <ColorTable
            headers={[
                t("colorPage.colVariant"),
                t("colorPage.colSurface"),
                t("colorPage.colLabel"),
                t("colorPage.colBorder"),
                t("colorPage.colLabelContrast")
            ]}
            rows={rows}
        />
    </View>;
};

const PaletteStrip = ({
    paletteKey,
    themeKey
}: {
    paletteKey: PaletteKeyType;
    themeKey: ThemeKeyType;
}) => {
    const {
        colors
    } = NCoreUIKitTheme.useContext({
        paletteKey,
        themeKey
    });

    const styles = useColorStyles();

    return <View
        style={[
            stylesheet.inline,
            styles.gapXs
        ]}
    >
        {
            PALETTE_STRIP.map((getColor, index) => <Swatch
                color={getColor(colors)}
                key={index}
            />)
        }
    </View>;
};

const PaletteTable = () => {
    const {
        t
    } = useDocsI18n();

    const {
        paletteKeys
    } = NCoreUIKitTheme.useContext();

    return <ColorTable
        rows={paletteKeys.map((paletteKey) => [
            PALETTE_NAMES[paletteKey] ?? paletteKey,
            <PaletteStrip
                paletteKey={paletteKey}
                themeKey="light"
                key="light"
            />,
            <PaletteStrip
                paletteKey={paletteKey}
                themeKey="dark"
                key="dark"
            />
        ])}
        headers={[
            t("colorPage.colPalette"),
            t("colorPage.colLight"),
            t("colorPage.colDark")
        ]}
    />;
};

const LadderTable = () => {
    const {
        t
    } = useDocsI18n();

    const {
        colors
    } = NCoreUIKitTheme.useContext({
        themeKey: "light"
    });

    const ground = colors.content.container.default;

    return <ColorTable
        rows={LADDER_ROWS.map((row) => {
            const color = row.getColor(colors);

            return [
                <TokenName
                    detail={`k${row.step}`}
                    name={row.name}
                    key="name"
                />,
                <ColorValue
                    color={color}
                    key="color"
                />,
                <ValueText
                    key="distance"
                >
                    {formatJnd(getJnd(color, ground, LIGHT_VIEWING))}
                </ValueText>,
                <ValueText
                    key="target"
                >
                    {formatJnd(row.step * LADDER_STEP)}
                </ValueText>,
                <ValueText
                    key="ratio"
                >
                    {formatRatio(getContrastRatio(color, ground))}
                </ValueText>
            ];
        })}
        headers={[
            t("colorPage.colToken"),
            t("colorPage.colColor"),
            t("colorPage.colFromWhite"),
            t("colorPage.colTarget"),
            t("colorPage.colOnGround")
        ]}
    />;
};

const DarkLadderTable = () => {
    const {
        t
    } = useDocsI18n();

    const {
        colors: darkColors
    } = NCoreUIKitTheme.useContext({
        themeKey: "dark"
    });

    const {
        colors: lightColors
    } = NCoreUIKitTheme.useContext({
        themeKey: "light"
    });

    return <ColorTable
        rows={DARK_ROWS.map((row) => {
            const color = row.getColor(darkColors);

            return [
                <InlineCode
                    key="name"
                >
                    {makeBreakable(row.name)}
                </InlineCode>,
                <ColorValue
                    color={color}
                    key="color"
                />,
                <ValueText
                    key="dark"
                >
                    {formatJnd(getJnd(color, darkColors.content.container.raised, DARK_VIEWING))}
                </ValueText>,
                <ValueText
                    key="light"
                >
                    {formatJnd(getJnd(row.getColor(lightColors), lightColors.content.container.raised, LIGHT_VIEWING))}
                </ValueText>,
                <ValueText
                    key="ratio"
                >
                    {formatRatio(getContrastRatio(color, darkColors.content.container.default))}
                </ValueText>
            ];
        })}
        headers={[
            t("colorPage.colToken"),
            t("colorPage.colColor"),
            t("colorPage.colFromCard"),
            t("colorPage.colLightFromWhite"),
            t("colorPage.colOnGround")
        ]}
    />;
};

const CardsPreview = () => {
    const {
        t
    } = useDocsI18n();

    const styles = useColorStyles();

    return <Preview
        caption={t("colorPage.cardsCaption")}
    >
        <div
            style={styles.cardGrid}
        >
            {
                CARD_VARIANTS.map((item) => <Card
                    spreadBehaviour="stretch"
                    variant={item.variant}
                    key={item.variant}
                >
                    <Card.Header>
                        <Card.Title>
                            {item.variant}
                        </Card.Title>
                        <Card.Description>
                            {t(item.textKey)}
                        </Card.Description>
                    </Card.Header>
                </Card>)
            }
        </div>
    </Preview>;
};

const ContrastTable = ({
    steps
}: {
    steps: Array<ContrastStepType>;
}) => {
    const {
        t
    } = useDocsI18n();

    const {
        colors
    } = NCoreUIKitTheme.useContext();

    const styles = useColorStyles();

    const ground = colors.content.container.default;

    const columns = steps.map((step) => FAMILIES.map((family) => {
        const color = step.getColor(colors, family);

        return {
            ratio: getContrastRatio(color, ground),
            color
        };
    }));

    return <ColorTable
        rows={[
            ...FAMILIES.map((family, familyIndex) => [
                family,
                ...columns.map((column, columnIndex) => {
                    const cell = column[familyIndex];

                    return cell ? <View
                        style={[
                            stylesheet.inline,
                            styles.gapSm
                        ]}
                        key={columnIndex}
                    >
                        <Swatch
                            color={cell.color}
                        />
                        <ValueText>
                            {cell.ratio.toFixed(2)}
                        </ValueText>
                    </View> : null;
                })
            ]),
            [
                t("colorPage.spreadRow"),
                ...columns.map((column, columnIndex) => <ValueText
                    key={columnIndex}
                >
                    {getSpread(column.map((cell) => cell.ratio)).toFixed(3)}
                </ValueText>)
            ]
        ]}
        headers={[
            t("colorPage.familyPickerLabel"),
            ...steps.map((step) => step.roles)
        ]}
    />;
};

const GreyCell = ({
    paletteKey,
    getColor
}: {
    paletteKey: PaletteKeyType;
    getColor: ColorPickType;
}) => {
    const {
        t
    } = useDocsI18n();

    const {
        colors
    } = NCoreUIKitTheme.useContext({
        paletteKey
    });

    const styles = useColorStyles();

    const color = getColor(colors);

    return <View
        style={[
            stylesheet.inline,
            styles.gapSm
        ]}
    >
        <Swatch
            color={color}
        />
        <View
            style={stylesheet.column}
        >
            <ValueText>
                {`${t("colorPage.colChroma")} ${getOklchChroma(color).toFixed(4)}`}
            </ValueText>
            <ValueText>
                {`${t("colorPage.colDuv")} ${formatSigned(getDeltaDuv(color))}`}
            </ValueText>
        </View>
    </View>;
};

const GreyTable = () => {
    const {
        t
    } = useDocsI18n();

    const {
        paletteKeys
    } = NCoreUIKitTheme.useContext();

    return <ColorTable
        headers={[
            t("colorPage.colToken"),
            ...paletteKeys.map((paletteKey) => PALETTE_NAMES[paletteKey] ?? paletteKey)
        ]}
        rows={GREY_TOKENS.map((token) => [
            <InlineCode
                key="name"
            >
                {makeBreakable(token.name)}
            </InlineCode>,
            ...paletteKeys.map((paletteKey) => <GreyCell
                getColor={token.getColor}
                paletteKey={paletteKey}
                key={paletteKey}
            />)
        ])}
    />;
};

const TextGreyTable = () => {
    const {
        t
    } = useDocsI18n();

    const {
        activeTheme,
        colors
    } = NCoreUIKitTheme.useContext();

    const surfaces = TEXT_SURFACES.map((getSurface) => getSurface(colors));

    return <ColorTable
        rows={TEXT_GREYS.map((row) => {
            const color = row.getColor(colors);

            const wcagGate = row.gate === "exempt" ? t("colorPage.gateExempt") : GATE_LABELS[row.gate];

            const apcaGate = activeTheme === "dark" && row.isApcaInfoInDark ? t("colorPage.gateApcaInfo", [
                row.apcaTarget
            ]) : `Lc ${row.apcaTarget}`;

            return [
                <InlineCode
                    key="name"
                >
                    {makeBreakable(row.name)}
                </InlineCode>,
                <ColorValue
                    color={color}
                    key="color"
                />,
                <ValueText
                    key="wcag"
                >
                    {formatRatio(Math.min(...surfaces.map((surface) => getContrastRatio(color, surface))))}
                </ValueText>,
                <ValueText
                    key="apca"
                >
                    {`Lc ${Math.min(...surfaces.map((surface) => Math.abs(getApcaContrast(color, surface)))).toFixed(1)}`}
                </ValueText>,
                `${wcagGate} · ${apcaGate}`
            ];
        })}
        headers={[
            t("colorPage.colToken"),
            t("colorPage.colColor"),
            t("colorPage.colWcagMin"),
            t("colorPage.colApcaMin"),
            t("colorPage.colGate")
        ]}
    />;
};

const AliasTable = () => {
    const {
        t
    } = useDocsI18n();

    const {
        colors
    } = NCoreUIKitTheme.useContext();

    return <ColorTable
        rows={ALIAS_ROWS.map((row) => {
            const aliasColor = row.getAlias(colors);

            const canonicalColor = row.getCanonical(colors);

            return [
                <InlineCode
                    key="alias"
                >
                    {makeBreakable(row.alias)}
                </InlineCode>,
                <InlineCode
                    key="canonical"
                >
                    {makeBreakable(row.canonical)}
                </InlineCode>,
                <ValueText
                    key="same"
                >
                    {aliasColor === canonicalColor ? t("colorPage.aliasSame") : t("colorPage.aliasDiffers", [
                        aliasColor,
                        canonicalColor
                    ])}
                </ValueText>
            ];
        })}
        headers={[
            t("colorPage.colAlias"),
            t("colorPage.colCanonical"),
            t("colorPage.colSame")
        ]}
    />;
};

const OverlayTable = () => {
    const {
        t
    } = useDocsI18n();

    const {
        activeTheme,
        colors
    } = NCoreUIKitTheme.useContext();

    const viewing = getThemeViewing(activeTheme);

    const ground = colors.content.container.default;

    const card = colors.content.container.subtle;

    return <ColorTable
        rows={OVERLAY_ROWS.map((row) => {
            const color = row.getColor(colors);

            return [
                <TokenName
                    name={row.name}
                    detail={color}
                    key="name"
                />,
                <Swatch
                    color={getCompositeColor(color, ground)}
                    key="result"
                />,
                <ValueText
                    key="ground"
                >
                    {formatJnd(getJnd(color, ground, viewing))}
                </ValueText>,
                <ValueText
                    key="card"
                >
                    {formatJnd(getJnd(color, card, viewing))}
                </ValueText>
            ];
        })}
        headers={[
            t("colorPage.colTokenValue"),
            t("colorPage.colResult"),
            t("colorPage.colLayerGround"),
            t("colorPage.colLayerCard")
        ]}
    />;
};

const ChartTiles = ({
    items
}: {
    items: Array<[string, string]>;
}) => {
    const {
        colors
    } = NCoreUIKitTheme.useContext();

    const styles = useColorStyles();

    return <View
        style={[
            stylesheet.wrapRow,
            styles.gapMd
        ]}
    >
        {
            items.map(([
                name,
                color
            ]) => <View
                style={[
                    stylesheet.column,
                    styles.gapXs,
                    styles.tile
                ]}
                key={name}
            >
                <Swatch
                    isLarge={true}
                    color={color}
                />
                <ValueText>
                    {name}
                </ValueText>
                <Text
                    variant="bodySmallSize"
                    color="mid"
                >
                    {formatRatio(getContrastRatio(color, colors.content.container.default))}
                </Text>
            </View>)
        }
    </View>;
};

const FamilyPreview = () => {
    const {
        t
    } = useDocsI18n();

    const styles = useColorStyles();

    return <Preview
        caption={t("colorPage.previewCaption")}
    >
        <div
            style={styles.familyGrid}
        >
            {
                FAMILIES.map((family) => <View
                    style={[
                        stylesheet.column,
                        styles.gapSm
                    ]}
                    key={family}
                >
                    <Text
                        variant="labelMediumSize"
                        color="mid"
                    >
                        {family}
                    </Text>
                    <Button
                        title={t("colorPage.buttonFilled")}
                        onPress={handlePreviewPress}
                        variant="filled"
                        type={family}
                        size="small"
                    />
                    <Button
                        title={t("colorPage.buttonTonal")}
                        onPress={handlePreviewPress}
                        variant="tonal"
                        type={family}
                        size="small"
                    />
                    <Button
                        title={t("colorPage.buttonOutline")}
                        onPress={handlePreviewPress}
                        variant="outline"
                        type={family}
                        size="small"
                    />
                    <Badge
                        variant="tonal"
                        title={family}
                        type={family}
                    />
                </View>)
            }
        </div>
    </Preview>;
};

const AlertsPreview = () => {
    const {
        t
    } = useDocsI18n();

    return <Preview
        caption={t("colorPage.alertsCaption")}
        isStacked={true}
    >
        {
            FAMILIES.map((family) => <Alert
                description={t("colorPage.alertText")}
                announceBehaviour="none"
                variant="tonal"
                title={family}
                type={family}
                key={family}
            />)
        }
    </Preview>;
};

export const getColorToc = (t: DocsTranslate): Array<DocsTocItem> => [
    {
        title: t("section.overview"),
        id: "overview"
    },
    {
        title: t("colorPage.palettesTitle"),
        id: "palettes"
    },
    {
        title: t("colorPage.brandTitle"),
        id: "brand"
    },
    {
        title: t("colorPage.greysTitle"),
        id: "greys"
    },
    {
        title: t("colorPage.ladderTitle"),
        id: "ladder"
    },
    {
        title: t("colorPage.contrastTitle"),
        id: "contrast"
    },
    {
        title: t("colorPage.statesTitle"),
        id: "states"
    },
    {
        title: t("colorPage.darkTitle"),
        id: "dark"
    },
    {
        title: t("colorPage.chartsTitle"),
        id: "charts"
    },
    {
        title: t("colorPage.codeTitle"),
        id: "code"
    },
    {
        title: t("colorPage.sourcesTitle"),
        id: "sources"
    }
];

const ColorPage = () => {
    const {
        t
    } = useDocsI18n();

    const {
        borders,
        colors
    } = NCoreUIKitTheme.useContext();

    const {
        colors: darkColors
    } = NCoreUIKitTheme.useContext({
        themeKey: "dark"
    });

    const ground = colors.content.container.default;

    const darkGround = darkColors.content.container.default;

    const primaryFill = getFilledColors(colors, "primary");

    const brandValues = [
        formatRatio(getContrastRatio(primaryFill.fill, ground)),
        formatRatio(getContrastRatio(primaryFill.label, primaryFill.fill)),
        formatRatio(getContrastRatio(colors.system.state.overlay.focus.default, ground)),
        borders.focus
    ];

    const marksValues = [
        formatRatio(getContrastRatio(colors.content.border.default, ground)),
        formatRatio(getContrastRatio(colors.content.icon.low, ground))
    ];

    const contrastValues = [
        formatRatio(getContrastRatio(darkColors.content.text.low, darkGround)),
        Math.abs(getApcaContrast(darkColors.content.text.low, darkGround)).toFixed(1)
    ];

    const iconStepValues = [
        formatJnd(getJnd(darkColors.content.icon.default, darkColors.content.icon.mid, DARK_VIEWING)),
        formatJnd(getJnd(darkColors.content.icon.mid, darkColors.content.icon.low, DARK_VIEWING))
    ];

    const brandLabelApca = BRAND_AND_STATUS_FAMILIES.map((family) => getFilledLabelApca(darkColors, family));

    const filledValues = [
        Math.min(...brandLabelApca).toFixed(1),
        Math.max(...brandLabelApca).toFixed(1),
        getFilledLabelApca(darkColors, "neutral").toFixed(1)
    ];

    const darkValues = [
        darkGround,
        darkColors.content.container.raised,
        formatJnd(getJnd(darkColors.content.container.raised, darkGround, DARK_VIEWING))
    ];

    return <Article>
        <PageHeader
            breadcrumbs={[
                {
                    label: t("breadcrumb.docs"),
                    href: "/"
                },
                {
                    label: t("nav.sectionFoundations")
                }
            ]}
            lead={t("colorPage.lead")}
            title={t("page.color")}
        />
        <Section
            title={t("section.overview")}
            id="overview"
        >
            <Paragraph>
                {t("colorPage.overviewText")}
            </Paragraph>
            <TermList
                items={[
                    "colorPage.overviewRoles",
                    "colorPage.overviewBrand",
                    "colorPage.overviewLadder",
                    "colorPage.overviewContrast",
                    "colorPage.overviewDark"
                ]}
            />
        </Section>
        <Section
            title={t("colorPage.palettesTitle")}
            id="palettes"
        >
            <Paragraph>
                {t("colorPage.palettesText")}
            </Paragraph>
            <PaletteTable/>
            <Paragraph
                isMuted={true}
            >
                {t("colorPage.palettesSwatchHint")}
            </Paragraph>
            <SubTitle>
                {t("colorPage.rolesTitle")}
            </SubTitle>
            <Paragraph>
                {t("colorPage.rolesText")}
            </Paragraph>
            <RoleGroupTable/>
            <SubTitle>
                {t("colorPage.familiesTitle")}
            </SubTitle>
            <Prose
                text={t("colorPage.familiesText")}
            />
            <VariantTable/>
        </Section>
        <Section
            title={t("colorPage.brandTitle")}
            id="brand"
        >
            <Paragraph>
                {t("colorPage.brandText")}
            </Paragraph>
            <TermList
                items={[
                    "colorPage.brandAction",
                    "colorPage.brandValue",
                    "colorPage.brandFocus"
                ]}
            />
            <Paragraph>
                {t("colorPage.brandNeutralText")}
            </Paragraph>
            <Paragraph>
                {t("colorPage.brandNumbersText", brandValues)}
            </Paragraph>
        </Section>
        <Section
            title={t("colorPage.greysTitle")}
            id="greys"
        >
            <Prose
                text={t("colorPage.greysText")}
            />
            <Prose
                text={t("colorPage.greysWhyText")}
            />
            <Prose
                text={t("colorPage.greysTestText")}
            />
            <Paragraph>
                {t("colorPage.greysPalettesText")}
            </Paragraph>
            <Paragraph
                isMuted={true}
            >
                {t("colorPage.greysTableText")}
            </Paragraph>
            <GreyTable/>
            <Paragraph>
                {t("colorPage.greysDataText")}
            </Paragraph>
        </Section>
        <Section
            title={t("colorPage.ladderTitle")}
            id="ladder"
        >
            <Prose
                text={t("colorPage.ladderText")}
            />
            <Prose
                text={t("colorPage.ladderMeasureText")}
            />
            <Paragraph>
                {t("colorPage.ladderWhyText")}
            </Paragraph>
            <Paragraph
                isMuted={true}
            >
                {t("colorPage.ladderTableText")}
            </Paragraph>
            <LadderTable/>
            <CardsPreview/>
        </Section>
        <Section
            title={t("colorPage.contrastTitle")}
            id="contrast"
        >
            <Prose
                text={t("colorPage.contrastWcagText")}
            />
            <Paragraph>
                {t("colorPage.contrastApcaText")}
            </Paragraph>
            <Paragraph>
                {t("colorPage.contrastWhyText", contrastValues)}
            </Paragraph>
            <Paragraph
                isMuted={true}
            >
                {t("colorPage.textTableText")}
            </Paragraph>
            <TextGreyTable/>
            <Callout
                title={t("colorPage.contrastStatusTitle")}
            >
                {t("colorPage.contrastStatusText", iconStepValues)}
            </Callout>
            <Paragraph>
                {t("colorPage.contrastFilledText", filledValues)}
            </Paragraph>
            <Paragraph>
                {t("colorPage.marksText", marksValues)}
            </Paragraph>
            <Paragraph
                isMuted={true}
            >
                {t("colorPage.contrastCodeText")}
            </Paragraph>
            <CodeBlock
                code={CONTRAST_CODE}
                language="ts"
            />
            <SubTitle>
                {t("colorPage.stepsTitle")}
            </SubTitle>
            <Prose
                text={t("colorPage.stepsText")}
            />
            <Paragraph>
                {t("colorPage.stepsFillsText")}
            </Paragraph>
            <Paragraph
                isMuted={true}
            >
                {t("colorPage.contrastTableText")}
            </Paragraph>
            <Paragraph
                isMuted={true}
            >
                {t("colorPage.lightFillsCaption")}
            </Paragraph>
            <ContrastTable
                steps={CONTRAST_STEPS.slice(0, 3)}
            />
            <Paragraph
                isMuted={true}
            >
                {t("colorPage.strongCaption")}
            </Paragraph>
            <ContrastTable
                steps={CONTRAST_STEPS.slice(3)}
            />
            <Paragraph>
                {t("colorPage.previewText")}
            </Paragraph>
            <FamilyPreview/>
            <AlertsPreview/>
        </Section>
        <Section
            title={t("colorPage.statesTitle")}
            id="states"
        >
            <Prose
                text={t("colorPage.statesText")}
            />
            <Paragraph>
                {t("colorPage.statesLadderText")}
            </Paragraph>
            <Paragraph
                isMuted={true}
            >
                {t("colorPage.statesTableText")}
            </Paragraph>
            <OverlayTable/>
            <Paragraph>
                {t("colorPage.statesFillText")}
            </Paragraph>
            <Paragraph>
                {t("colorPage.statesDisabledText")}
            </Paragraph>
            <Paragraph>
                {t("colorPage.statesTypedText")}
            </Paragraph>
        </Section>
        <Section
            title={t("colorPage.darkTitle")}
            id="dark"
        >
            <Prose
                text={t("colorPage.darkText")}
            />
            <Paragraph>
                {t("colorPage.darkStepsText", darkValues)}
            </Paragraph>
            <Paragraph
                isMuted={true}
            >
                {t("colorPage.darkTableText")}
            </Paragraph>
            <DarkLadderTable/>
            <Paragraph>
                {t("colorPage.darkMidText")}
            </Paragraph>
        </Section>
        <Section
            title={t("colorPage.chartsTitle")}
            id="charts"
        >
            <Paragraph>
                {t("colorPage.chartsText")}
            </Paragraph>
            <SubTitle>
                {t("colorPage.categoricalTitle")}
            </SubTitle>
            <Prose
                text={t("colorPage.categoricalText")}
            />
            <ChartTiles
                items={Object.entries(colors.chart.categorical)}
            />
            <Paragraph>
                {t("colorPage.categoricalCvdText")}
            </Paragraph>
            <SubTitle>
                {t("colorPage.mutedTitle")}
            </SubTitle>
            <Paragraph>
                {t("colorPage.mutedText")}
            </Paragraph>
            <ChartTiles
                items={[
                    [
                        "muted",
                        colors.chart.muted
                    ]
                ]}
            />
            <SubTitle>
                {t("colorPage.divergingTitle")}
            </SubTitle>
            <Prose
                text={t("colorPage.divergingText")}
            />
            <ChartTiles
                items={Object.entries(colors.chart.diverging)}
            />
            <SubTitle>
                {t("colorPage.sequentialTitle")}
            </SubTitle>
            <Prose
                text={t("colorPage.sequentialText")}
            />
            <ChartTiles
                items={Object.entries(colors.chart.sequential)}
            />
            <SubTitle>
                {t("colorPage.statusTitle")}
            </SubTitle>
            <Paragraph>
                {t("colorPage.statusText")}
            </Paragraph>
            <ChartTiles
                items={[
                    [
                        "success",
                        colors.content.icon.success
                    ],
                    [
                        "warning",
                        colors.content.icon.warning
                    ],
                    [
                        "danger",
                        colors.content.icon.danger
                    ]
                ]}
            />
        </Section>
        <Section
            title={t("colorPage.codeTitle")}
            id="code"
        >
            <Paragraph>
                {t("colorPage.codeText")}
            </Paragraph>
            <CodeBlock
                code={COLOR_CODE}
                language="ts"
            />
            <Paragraph>
                {t("colorPage.codeComponentsText")}
            </Paragraph>
            <CodeBlock
                code={COMPONENT_CODE}
                language="tsx"
            />
            <Paragraph>
                {t("colorPage.codeRulesText")}
            </Paragraph>
            <BulletList
                items={[
                    t("colorPage.codeRuleSelected"),
                    t("colorPage.codeRuleOverlay"),
                    t("colorPage.codeRuleMarks"),
                    t("colorPage.codeRuleValues")
                ]}
            />
            <SubTitle>
                {t("colorPage.aliasesTitle")}
            </SubTitle>
            <Paragraph>
                {t("colorPage.aliasesText")}
            </Paragraph>
            <AliasTable/>
            <Paragraph>
                {
                    t("colorPage.disabledWhiteText", [
                        colors.content.text.disabledWhite
                    ])
                }
            </Paragraph>
        </Section>
        <Section
            title={t("colorPage.sourcesTitle")}
            id="sources"
        >
            <BulletList
                items={[
                    t("colorPage.sourceWcag"),
                    t("colorPage.sourceApca"),
                    t("colorPage.sourceCam16"),
                    t("colorPage.sourceOhno"),
                    t("colorPage.sourceOklab"),
                    t("colorPage.sourceMachado")
                ]}
            />
        </Section>
    </Article>;
};
export default ColorPage;
